import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { KkPdfData } from "../../types/CityObjectPdfData";
import { formatFinnishDate, formatNumber } from "../../utilities/PdfUtils";

const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 9, fontFamily: "Helvetica" },
  title: { fontSize: 14, fontWeight: "bold", marginBottom: 2 },
  subtitle: { fontSize: 11, fontWeight: "demibold", marginBottom: 4 },
  divider: { borderBottomWidth: 1, borderBottomColor: "#999", marginBottom: 10 },
  row: { flexDirection: "row", marginBottom: 3 },
  labelCell: { width: "15%", fontWeight: "bold" },
  valueCell: { width: "35%" },
  sectionTitle: { fontSize: 11, fontWeight: "bold", marginTop: 12, marginBottom: 6 },
  tableHeader: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#ccc", paddingBottom: 2, marginBottom: 4 },
  tableRow: { flexDirection: "row", marginBottom: 2 },
  colName: { width: "30%" },
  colUnit: { width: "10%" },
  colNum: { width: "10%", textAlign: "right" },
  bold: { fontWeight: "bold" },
  description: { marginTop: 6 },
  footer: { position: "absolute", bottom: 20, left: 30, right: 30, textAlign: "center", fontSize: 8, color: "#666" },
});

interface Props {
  data: KkPdfData;
}

const InfoRow = ({ l1, v1, l2, v2 }: { l1: string; v1: string; l2: string; v2: string }) => (
  <View style={styles.row}>
    <Text style={styles.labelCell}>{l1}</Text>
    <Text style={styles.valueCell}>{v1}</Text>
    <Text style={styles.labelCell}>{l2}</Text>
    <Text style={styles.valueCell}>{v2}</Text>
  </View>
);

const KkPDF = ({ data }: Props) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Text style={styles.title}>Inspection Card (KK) — #{data.perceptionId}</Text>
      <Text style={styles.subtitle}>{data.location}</Text>
      <View style={styles.divider} />

      <InfoRow l1="Contract" v1={data.contractName} l2="Contractor" v2={data.contractorName ?? "—"} />
      <InfoRow l1="Status" v1={data.statusLabel} l2="Type" v2={data.strengthLabel ?? "—"} />
      <InfoRow l1="Planned date" v1={formatFinnishDate(data.plannedDate)} l2="Start date" v2={formatFinnishDate(data.startDate)} />
      <InfoRow l1="End date" v1={formatFinnishDate(data.endDate)} l2="Ready date" v2={formatFinnishDate(data.readyDate)} />
      <InfoRow l1="Foreman" v1={data.foremanName ?? "—"} l2="Subscriber" v2={data.subscriberName ?? "—"} />
      <InfoRow l1="Order no." v1={data.orderNumber ?? "—"} l2="Network no." v2={data.networkNumber ?? "—"} />
      <InfoRow l1="Site no." v1={data.siteNumber ?? "—"} l2="Lanes" v2={data.lanes ?? "—"} />
      <InfoRow l1="Street" v1={data.streetName ?? "—"} l2="Created" v2={formatFinnishDate(data.created)} />
      <InfoRow l1="Length (m)" v1={data.objectLength?.toFixed(1) ?? "—"} l2="Width (m)" v2={data.objectWidth?.toFixed(1) ?? "—"} />
      <InfoRow l1="Meas. date" v1={formatFinnishDate(data.measurementDate)} l2="Meas. by" v2={data.measurementPersons ?? "—"} />

      {data.description ? (
        <View style={styles.description}>
          <Text style={styles.bold}>Description</Text>
          <Text>{data.description}</Text>
        </View>
      ) : null}

      {data.typeComment ? (
        <View style={styles.description}>
          <Text style={styles.bold}>Type comment</Text>
          <Text>{data.typeComment}</Text>
        </View>
      ) : null}

      {data.workSteps.length > 0 && (
        <View>
          <Text style={styles.sectionTitle}>Work Steps</Text>
          <View style={styles.tableHeader}>
            <Text style={[styles.colName, styles.bold]}>Item</Text>
            <Text style={[styles.colUnit, styles.bold]}>Unit</Text>
            <Text style={[styles.colNum, styles.bold]}>Qty</Text>
            <Text style={[styles.colNum, styles.bold]}>Area</Text>
            <Text style={[styles.colNum, styles.bold]}>Weight</Text>
            <Text style={[styles.colNum, styles.bold]}>Price</Text>
            <Text style={[styles.colNum, styles.bold]}>Date</Text>
          </View>
          {data.workSteps.map((ws, i) => (
            <View key={i} style={styles.tableRow}>
              <Text style={styles.colName}>{ws.itemName}</Text>
              <Text style={styles.colUnit}>{ws.unitName ?? ""}</Text>
              <Text style={styles.colNum}>{formatNumber(ws.quantity)}</Text>
              <Text style={styles.colNum}>{formatNumber(ws.area)}</Text>
              <Text style={styles.colNum}>{formatNumber(ws.weight)}</Text>
              <Text style={styles.colNum}>{formatNumber(ws.unitPrice)}</Text>
              <Text style={styles.colNum}>{formatFinnishDate(ws.itemDate)}</Text>
            </View>
          ))}
        </View>
      )}

      <Text style={styles.footer} render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} fixed />
    </Page>
  </Document>
);

export default KkPDF;
